import { useState } from 'react';
import { PaperclipIcon } from 'lucide-react';
import { useNamespaceCopy } from '@/i18n/ns';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import renderKitCopy from '@/i18n/ns/render-kit';
import { formatEmailDate, mailbox, type EmailView } from './email-model';

const LONG_BODY = 1200;
const LONG_LINES = 18;

export function Sender({ value }: { value: string }) {
  const { name, address } = mailbox(value);
  return (
    <span className="pc-email-sender">
      <span className="pc-email-avatar" aria-hidden="true">
        {(name || address).slice(0, 1).toUpperCase()}
      </span>
      <span className="pc-email-sender-text">
        <span className="pc-email-sender-name">{name}</span>
        {address && address !== name && <span className="pc-email-sender-address">{address}</span>}
      </span>
    </span>
  );
}

export function EmailBody({ body, shortened = false }: { body: string; shortened?: boolean }) {
  const copy = useNamespaceCopy(renderKitCopy);
  const long = body.length > LONG_BODY || body.split('\n').length > LONG_LINES;
  const [open, setOpen] = useState(false);
  return (
    <div className="pc-email-body-wrap">
      <p className="pc-email-body" data-clamped={long && !open ? 'true' : undefined}>
        {body}
      </p>
      {/* The server cut this text to its size limit: never let it look whole. */}
      {shortened && (!long || open) && <p className="pc-email-shortened">{copy.bodyShortened}</p>}
      {long && (
        <button
          type="button"
          className="pc-email-more"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          {open ? copy.showLess : copy.showMore}
        </button>
      )}
    </div>
  );
}

/** One received message: who, when, to whom, then its text and attachment names. */
export function EmailMessage({ email }: { email: EmailView }) {
  const copy = useNamespaceCopy(renderKitCopy);
  const locale = useChatInterfaceLocale();
  return (
    <div className="pc-email-message">
      <div className="pc-email-meta">
        <Sender value={email.from} />
        {email.date && (
          <time className="pc-email-date" dateTime={email.date}>
            {formatEmailDate(email.date, locale)}
          </time>
        )}
      </div>
      <dl className="pc-email-recipients">
        {email.to && (
          <div>
            <dt>{copy.to}</dt>
            <dd>{email.to}</dd>
          </div>
        )}
        {email.cc && (
          <div>
            <dt>{copy.cc}</dt>
            <dd>{email.cc}</dd>
          </div>
        )}
      </dl>
      <EmailBody body={email.body} shortened={email.bodyTruncated} />
      {email.attachments.length > 0 && (
        <ul className="pc-email-attachments" aria-label={copy.attachments}>
          {email.attachments.map((item, index) => (
            <li key={`${item.filename}:${index}`}>
              <PaperclipIcon aria-hidden="true" />
              <span>{item.filename}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
